import React, {useLayoutEffect, useState} from 'react';
import {
    Button,
    Card,
    CardBody,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    Input,
    Typography
} from "@material-tailwind/react";
import {NumericFormat} from "react-number-format";
import axios from "axios";
import LotSummary from "./lot_summary";

// Motivo 21 - AFECTACION: reducción de superficie de terrenos y/o construcciones del
// predio (obra o utilidad pública). Solo permite reducir las superficies existentes;
// el backend recalcula avalúo y regenera adeudos. Registro únicamente interno.
const Affectation = ({lot, movement}) => {
    const [error, setError] = useState(null);
    const [loaderActive, setLoaderActive] = useState(false);
    const [openEndDialog, setOpenEndDialog] = useState(false);
    const [currentLot, setCurrentLot] = useState([]);
    const [assignedLands, setAssignedLands] = useState({});
    const [assignedConstructions, setAssignedConstructions] = useState({});

    const getCSRFToken = () => {
        const token = document.querySelector('meta[name="csrf-token"]');
        return token ? token.content : "";
    };

    const handleReset = () => {
        window.location.replace('movements');
    };

    useLayoutEffect(() => {
        setCurrentLot(lot);

        async function fetchData() {
            try {
                const [constructionsResponse, landResponse] = await Promise.all([
                    fetch(`/lots/${lot.parcel_code}/constructions.json`),
                    fetch(`/lots/${lot.parcel_code}/land.json`),
                ]);

                if (landResponse.ok) {
                    const landData = await landResponse.json();

                    const initAssignments = {};
                    landData.forEach((land) => {
                        initAssignments[land.id] = {
                            area: land.area,
                            current_area: land.area,
                            rustic_land_value_id: land.rustic_land_value_id,
                            rustic_land_desc: land.rustic_land_desc,
                        };
                    });
                    setAssignedLands(initAssignments);
                } else {
                    console.log("No se encontraron terrenos para este código.");
                }

                if (constructionsResponse.ok) {
                    const constructionsData = await constructionsResponse.json();

                    const initConstAssignments = {};
                    constructionsData.forEach((construction) => {
                        initConstAssignments[construction.construction_id] = {
                            area: construction.area,
                            current_area: construction.area,
                            construction_devaluation_id: construction.construction_devaluation_id,
                            construction_value_id: construction.construction_value_id,
                        };
                    });
                    setAssignedConstructions(initConstAssignments);
                } else {
                    console.log("No se encontraron construcciones para este código.");
                }
            } catch (err) {
                console.error("Error al buscar el predio:", err);
            }
        }

        fetchData();
    }, [lot]);

    const handleLandChange = (id, value) => {
        setAssignedLands((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                area: value,
            },
        }));
    };

    const handleConstructionChange = (id, value) => {
        setAssignedConstructions((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                area: value,
            },
        }));
    };

    const changedLands = Object.entries(assignedLands)
        .filter(([_, data]) => Number(data.area) !== Number(data.current_area));
    const changedConstructions = Object.entries(assignedConstructions)
        .filter(([_, data]) => Number(data.area) !== Number(data.current_area));

    const validate = () => {
        if (changedLands.length === 0 && changedConstructions.length === 0) {
            return 'Modifica al menos una superficie para registrar la afectación.';
        }
        const invalid = [...changedLands, ...changedConstructions].some(([_, data]) =>
            Number(data.area) <= 0 || Number(data.area) > Number(data.current_area)
        );
        if (invalid) {
            return 'La superficie afectada debe ser mayor a 0 y menor o igual a la actual (solo reducción).';
        }
        return null;
    };

    const handleSubmit = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);
        setLoaderActive(true);

        const updatedLands = changedLands.map(([id, data]) => ({
            id: Number(id),
            area: data.area,
        }));

        const updatedConstructions = changedConstructions.map(([id, data]) => ({
            id: Number(id),
            area: data.area,
            construction_devaluation_id: data.construction_devaluation_id,
            construction_value_id: data.construction_value_id,
        }));

        try {
            const response = await axios.post(`lots/${lot.parcel_code}/revaluation.json`, {
                lot: {
                    notes: currentLot.notes,
                    reason_type_id: movement.id,
                    effective_bimester: currentLot.effective_bimester,
                    effective_year: currentLot.effective_year,
                },
                updated_lands: updatedLands,
                updated_constructions: updatedConstructions,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    "X-CSRF-Token": getCSRFToken(),
                }
            });
            if (response.status === 200) {
                setOpenEndDialog(true);
                const current_lot_url = `/lots/${lot.parcel_code}/print_movement.pdf`;
                window.open(current_lot_url, '_blank');
                setLoaderActive(false);
            } else {
                setError('Error al registrar la afectación. Verifica los datos.');
                setLoaderActive(false);
            }
        } catch (err) {
            setError('Error al guardar. Verifica los datos.');
            setLoaderActive(false);
        }
    };

    const totalArea = Object.values(assignedLands).reduce((acc, land) => acc + Number(land.area || 0), 0);
    const totalConstruction = Object.values(assignedConstructions).reduce((acc, construction) => acc + Number(construction.area || 0), 0);
    const getUnit = lot.lot_location_type === 'URBANO' ? <>m<sup>2</sup></> : <>ha</>;

    return (
        <>
            <div className="flex-auto py-6 pt-0">
                <h6 className="font-semibold uppercase bg-blue-500 text-white p-2 rounded-t-lg">
                    Información del predio
                </h6>
                <div className="flex flex-col lg:flex-row gap-4 min-h-[350px]">
                    <div
                        className="flex-grow border border-blue-gray-100 p-4 rounded-t-none rounded-b-lg w-full lg:w-1/2 overflow-x-hidden">
                        <LotSummary lot={lot}/>
                    </div>
                </div>
            </div>
            <div className="flex-auto py-10 pt-0">
                <h6 className="font-semibold uppercase bg-blue-500 text-white p-2 rounded-t-lg">
                    Afectación
                </h6>
                <Card className='w-full border border-blue-gray-100 rounded-b-lg rounded-t-none' shadow={false}>
                    <CardBody className='p-4'>
                        <h6 className="text-blue-gray-600 text-sm mt-3 mb-2 font-bold uppercase">
                            TERRENOS
                        </h6>
                        <table className="w-full min-w-[640px] table-auto border">
                            <thead>
                            <tr className='bg-gray-50'>
                                {["Cve. Val.", "Descripción", "Área actual", "Área afectada"].map(
                                    (el) => (
                                        <th
                                            key={el}
                                            className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                        >
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase text-blue-gray-400"
                                            >
                                                {el}
                                            </Typography>
                                        </th>
                                    )
                                )}
                            </tr>
                            </thead>
                            <tbody>
                            {Object.entries(assignedLands).length === 0 ? (
                                <tr className="w-full">
                                    <td colSpan="100%" className="text-center py-3">
                                        <strong>El predio no cuenta con terrenos registrados.</strong>
                                    </td>
                                </tr>
                            ) : (Object.entries(assignedLands).map(([landId, data], index, array) => {
                                    const className = `py-3 px-5 ${
                                        index === array.length - 1 ? "" : "border-b border-blue-gray-50"
                                    }`;
                                    return (
                                        <tr key={landId} className="hover:bg-gray-50">
                                            <td className={className}>{data.rustic_land_value_id ? data.rustic_land_value_id : 'N/A'}</td>
                                            <td className={className}>{data.rustic_land_desc ? data.rustic_land_desc : 'Terreno urbano'}</td>
                                            <td className={className}>
                                                <NumericFormat
                                                    value={data.current_area}
                                                    displayType={'text'}
                                                    thousandSeparator=","
                                                /> {getUnit}
                                            </td>
                                            <td className={className}>
                                                <Input
                                                    value={data.area || 0.0}
                                                    onChange={(e) => handleLandChange(landId, e.target.value)}
                                                    containerProps={{className: '!min-w-0'}}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                            <tfoot>
                            <tr className='border-t'>
                                <td colSpan='3'
                                    className='py-3 px-5 text-right border-r'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Totales
                                    </Typography>
                                </td>
                                <td className='py-3 px-5 bg-gray-50 border-b'>
                                    <NumericFormat
                                        value={totalArea.toFixed(4)}
                                        displayType={'text'}
                                        allowLeadingZeros
                                        thousandSeparator=","
                                    /> {getUnit}
                                </td>
                            </tr>
                            </tfoot>
                        </table>
                        <hr className="my-6 border-b-1 border-blue-gray-100"/>
                        <h6 className="text-blue-gray-600 text-sm mt-3 mb-2 font-bold uppercase">
                            CONSTRUCCIONES
                        </h6>
                        <table className="w-full min-w-[640px] table-auto border">
                            <thead>
                            <tr className='bg-gray-50'>
                                {["Cve. Valuación", "Edad", "Sup. actual", "Sup. afectada"].map(
                                    (el) => (
                                        <th
                                            key={el}
                                            className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                        >
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase text-blue-gray-400"
                                            >
                                                {el}
                                            </Typography>
                                        </th>
                                    )
                                )}
                            </tr>
                            </thead>
                            <tbody>
                            {Object.entries(assignedConstructions).length === 0 ? (
                                <tr className="w-full">
                                    <td colSpan="100%" className="text-center py-3">
                                        <strong>El predio no cuenta con construcciones.</strong>
                                    </td>
                                </tr>
                            ) : (Object.entries(assignedConstructions).map(([constructionId, data], index, array) => {
                                    const className = `py-3 px-5 ${
                                        index === array.length - 1 ? "" : "border-b border-blue-gray-50"
                                    }`;
                                    return (
                                        <tr key={constructionId} className="hover:bg-gray-50">
                                            <td className={className}>{data.construction_value_id}</td>
                                            <td className={className}>{data.construction_devaluation_id}</td>
                                            <td className={className}>
                                                <NumericFormat
                                                    value={data.current_area}
                                                    displayType={'text'}
                                                    thousandSeparator=","
                                                /> m<sup>2</sup>
                                            </td>
                                            <td className={className}>
                                                <Input
                                                    value={data.area || 0}
                                                    onChange={(e) => handleConstructionChange(constructionId, e.target.value)}
                                                    containerProps={{className: "!min-w-0"}}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                            <tfoot>
                            <tr className='border-t'>
                                <td colSpan='3'
                                    className='py-3 px-5 text-right border-r'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Totales
                                    </Typography>
                                </td>
                                <td className='py-3 px-5 bg-gray-50 border-b'>
                                    <NumericFormat
                                        value={totalConstruction}
                                        displayType={'text'}
                                        thousandSeparator=","
                                    /> m<sup>2</sup>
                                </td>
                            </tr>
                            </tfoot>
                        </table>
                        <hr className="my-6 border-b-1 border-blue-gray-100"/>
                        <div className='py-2'>
                            <Typography
                                variant="small"
                                className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                            >
                                Fecha efecto
                            </Typography>
                            <div className='flex flex-row gap-4 w-full text-center'>
                                <div className='flex flex-col w-full'>
                                    <Input value={currentLot.effective_bimester}
                                           onChange={(e) => setCurrentLot({
                                               ...currentLot,
                                               effective_bimester: e.target.value,
                                           })}
                                           label={'Bim'}
                                           containerProps={{
                                               className: '!min-w-0'
                                           }}
                                    />
                                </div>
                                <div className='flex flex-col w-full'>
                                    <Input value={currentLot.effective_year}
                                           onChange={(e) => setCurrentLot({
                                               ...currentLot,
                                               effective_year: e.target.value,
                                           })}
                                           label={'Año'}
                                           containerProps={{
                                               className: '!min-w-0'
                                           }}
                                    />
                                </div>
                            </div>
                        </div>
                        <hr className="my-6 border-b-1 border-blue-gray-100"/>
                        <h6 className="text-blue-gray-300 text-sm mt-3 mb-6 font-bold uppercase">
                            NOTAS DE MOVIMIENTOS DEL PREDIO
                        </h6>
                        <div className="flex flex-wrap">
                            <div className="w-full lg:w-12/12 px-0">
                                <div className="relative w-full mb-3">
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                                    >
                                        Notas
                                    </Typography>
                                    <textarea
                                        type="text"
                                        className="border-0 px-3 py-3 placeholder-blueGray-300 text-blue-gray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                        value={currentLot.notes || ""}
                                        onChange={(e) => setCurrentLot({
                                            ...currentLot,
                                            notes: e.target.value,
                                        })}
                                        rows="6"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        <Button
                            className="w-full bg-green-500 mt-4 hover:bg-green-600 text-white rounded-md px-5 py-3 font-semibold"
                            onClick={() => handleSubmit()}
                            disabled={loaderActive}
                        >
                            GUARDAR
                        </Button>
                        {
                            error && <p style={{color: "red"}}>{error}</p>
                        }
                    </CardBody>
                </Card>
            </div>
            <Dialog open={openEndDialog} handler={setOpenEndDialog}>
                <DialogHeader>Guardado</DialogHeader>
                <DialogBody>
                    <Typography>
                        Se registró la afectación del predio
                    </Typography>
                </DialogBody>
                <DialogFooter>
                    <Button
                        className="bg-green-500 text-white"
                        onClick={() => handleReset()}
                        disabled={loaderActive}
                    >
                        Finalizar
                    </Button>
                </DialogFooter>
            </Dialog>
        </>
    )
};

export default Affectation;
